import { useEffect, useState } from 'react';
import { getColor, roundOffValue } from '../helpers/helpers';
import { Chart, ChartOptions, ChartTypeRegistry, registerables } from 'chart.js';
import { Dataset, DonutGraphDataSet } from '../types/types';
import _ from 'lodash';
import { AreaGraphProps } from 'Components/molecules/AreaGraph/AreaGraph';
import { LineGraphProps } from 'Components/molecules/LineGraph/LineGraph';
import { UserLineGraphProps } from 'Components/molecules/UserLineGraph/UserLineGraph';
import { EMPTY_LABELS } from './constants';

const calculateMinYaxisValue = (dataPoints: (number | null)[]) => {
    const compactDataPoints = _.compact(dataPoints);
    if (compactDataPoints.length === 0) {
        return 0;
    }
    const minValue = Math.min(...compactDataPoints);
    const minYaxisValue = Math.floor(minValue / 10) * 10 < 0 ? 0 : Math.floor(minValue / 10) * 10;
    return minYaxisValue;
};

const calculateMaxYaxisValue = (dataPoints: (number | null)[]) => {
    const compactDataPoints = _.compact(dataPoints);
    if (compactDataPoints.length === 0) {
        return 0;
    }
    const maxValue = Math.max(...compactDataPoints);
    const maxYaxisValue = Math.floor((maxValue + 10) / 10) * 10 || 0;
    return maxYaxisValue;
};

export const lineGraphData = (props: LineGraphProps) => {
    const [color, setColor] = useState('');
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth();
    const categories: string[] = [];
    const data = props.series.map((val: number) => (val > 0 ? val : null));

    if (props.categories) {
        categories.push(...props.categories);
    } else {
        for (let i = currentMonth - 5; i <= currentMonth; i++) {
            const monthIndex = i >= 0 ? i : 12 + i;
            categories.push(months[monthIndex]);
        }
    }

    return {
        series: [
            {
                data: data,
            },
        ],
        options: {
            chart: {
                height: 350,
                toolbar: {
                    show: false,
                },
            },
            dataLabels: {
                enabled: props.showDataLabel ?? true,
                style: {
                    colors: [color],
                    width: '8.75%',
                    height: '13%',
                    fontSize: '15px',
                },
                background: {
                    enabled: true,
                    foreColor: '#000000',
                },
                formatter: function (val: number, { dataPointIndex, w }: any) {
                    const value = w.config.series[0].data[dataPointIndex];

                    setColor(getColor(value));

                    if (dataPointIndex === w.config.series[0].data.length - 1) {
                        w.config.dataLabels.style.colors = [color];
                        return roundOffValue(val);
                    }
                    return '';
                },
            },
            xaxis: {
                categories: categories.map((str) => str.toUpperCase()),
                labels: {
                    style: {
                        fontFamily: 'Barlow',
                        fontSize: '10px',
                        fontWeight: 700,
                        lineHeight: '14px',
                        colors: '#FFFFFF',
                    },
                },
                tooltip: {
                    enabled: false,
                },
            },
            yaxis: {
                min: calculateMinYaxisValue(data),
                max: calculateMaxYaxisValue(data),
                tickAmount: 4,
                forceNiceScale: true,
                labels: {
                    style: {
                        fontFamily: 'Barlow',
                        fontSize: '10px',
                        fontWeight: 700,
                        lineHeight: '14px',
                        colors: ['#FFFFFF'],
                    },
                },
            },
            legend: {
                show: false,
            },
            colors: ['#9975FF'],
            stroke: {
                width: [3],
            },
            grid: {
                yaxis: {
                    lines: {
                        show: false,
                    },
                },
            },
            tooltip: {
                enabled: true,
                y: {
                    formatter: function (val: number, { dataPointIndex }: any) {
                        const index = dataPointIndex >= 0 ? dataPointIndex : categories.length + dataPointIndex;
                        return `${categories[index]} Score (${val})`;
                    },
                },
                x: {
                    show: false,
                },
                marker: {
                    show: false,
                },
            },
        },
    };
};

export const UserLineGraphData = (props: UserLineGraphProps) => {
    const [color, setColor] = useState('');
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth();
    const categories: string[] = [];
    const seriesData = props.series.map((data) => ({
        name: data?.name,
        data: Array.isArray(data.data) ? data.data.map((val) => (val > 0 ? val : null)) : [],
    }));
    const allDataPoints = seriesData.reduce((acc: (number | null)[], seriesItem) => acc.concat(seriesItem.data), []);

    if (props.categories) {
        categories.push(...props.categories);
    } else {
        for (let i = currentMonth - 5; i <= currentMonth; i++) {
            const monthIndex = i >= 0 ? i : 12 + i;
            categories.push(months[monthIndex]);
        }
    }
    const lineColors = props.lineColors ?? ['#FFFFFF', '#9975FF', '#958FA3'];

    return {
        series: seriesData,
        options: {
            chart: {
                height: 350,
                toolbar: {
                    show: false,
                },
            },
            dataLabels: {
                enabled: props.showDataLabels ?? true,
                enabledOnSeries: [0],
                style: {
                    colors: [color],
                    width: '8.75%',
                    height: '13%',
                    fontSize: '15px',
                },
                background: {
                    enabled: true,
                    foreColor: '#000000',
                },
                formatter: function (val: number, { dataPointIndex, w }: any) {
                    const value = w.config.series[0].data[dataPointIndex];

                    setColor(getColor(value));

                    if (dataPointIndex === w.config.series[0].data.length - 1) {
                        w.config.dataLabels.style.colors = [color];
                        return roundOffValue(val);
                    }
                    return '';
                },
            },
            xaxis: {
                categories: categories.map((str) => str.toUpperCase()),
                labels: {
                    style: {
                        fontFamily: 'Barlow',
                        fontSize: '10px',
                        fontWeight: 700,
                        lineHeight: '14px',
                        colors: '#FFFFFF',
                    },
                },
                tooltip: {
                    enabled: false,
                },
                crosshairs: {
                    show: false,
                },
            },
            yaxis: {
                min: calculateMinYaxisValue(allDataPoints),
                max: calculateMaxYaxisValue(allDataPoints),
                tickAmount: 4,
                forceNiceScale: true,
                labels: {
                    style: {
                        fontFamily: 'Barlow',
                        fontSize: '10px',
                        fontWeight: 700,
                        lineHeight: '14px',
                        colors: ['#FFFFFF'],
                    },
                },
            },
            legend: {
                show: false,
            },
            colors: lineColors,
            stroke: {
                width: props.width ?? [4, 2, 2],
            },
            grid: {
                yaxis: {
                    lines: {
                        show: false,
                    },
                },
            },
            tooltip: {
                enabled: true,
                shared: false,
                y: {
                    formatter: function (val: number, { seriesIndex, dataPointIndex }: any) {
                        const seriesDataPoint = seriesData[seriesIndex].data[dataPointIndex];
                        const seriesName = seriesData[seriesIndex].name;
                        return `${seriesName} Score (${seriesDataPoint})`;
                    },
                },
                x: {
                    show: false,
                },
                marker: {
                    show: false,
                },
                onDatasetHover: {
                    highlightDataSeries: true,
                },
            },
            markers: {
                hover: {
                    size: 0,
                },
            },
        },
    };
};

Chart.register(...registerables);

export const useRadarChartEffect = (chartRef: React.RefObject<HTMLCanvasElement>, datasets: Dataset[]) => {
    useEffect(() => {
        let chart: Chart;

        if (chartRef.current) {
            const ctx = chartRef.current.getContext('2d');

            if (ctx) {
                const labels = EMPTY_LABELS;

                chart = new Chart(ctx, {
                    type: 'radar',
                    data: {
                        labels: labels,
                        datasets: datasets.map((dataset) => ({
                            data: dataset.data.slice(0, 4),
                            backgroundColor: dataset.backgroundColor,
                            borderColor: dataset.borderColor,
                            borderWidth: 1,
                        })),
                    },
                    options: {
                        plugins: {
                            legend: {
                                display: false,
                            },
                            tooltip: {
                                enabled: true,
                                callbacks: {
                                    label: (context: any) => {
                                        const dataName = datasets[context.datasetIndex as number].name;
                                        return ` ${dataName} `;
                                    },
                                },
                                backgroundColor: '#d1cae5',
                                bodyColor: '#000000',
                                intersect: false,
                                interaction: {
                                    mode: 'nearest',
                                },
                            },
                        },
                        scales: {
                            r: {
                                angleLines: {
                                    color: '#38373B',
                                    lineWidth: 1,
                                },
                                grid: {
                                    circular: true,
                                    color: '#38373B',
                                    lineWidth: 1,
                                    stepSize: 25,
                                },
                                ticks: {
                                    display: false,
                                    min: 0,
                                    max: 100,
                                    stepSize: 25,
                                },
                                suggestedMin: 0,
                                suggestedMax: 100,
                                startAngle: 45,
                            },
                        },
                        elements: {
                            point: {
                                radius: 0,
                            },
                        },
                    } as ChartOptions<'radar'>,
                });

                datasets.forEach((dataset, index) => {
                    chart.data.datasets[index].data = dataset.data;
                });

                chart.update();
            }
        }

        return () => {
            if (chart) {
                chart.destroy();
            }
        };
    }, [chartRef, datasets]);
};

export const useDonutChartGraphData = (chartRef: React.RefObject<HTMLCanvasElement>, datasets: DonutGraphDataSet[]) => {
    useEffect(() => {
        let chart: Chart;
        if (chartRef.current) {
            const ctx = chartRef.current.getContext('2d');
            if (ctx) {
                const labels = ['TOOLING EXPOSURE', 'INCOMPLETE TRAINING', 'VULNERABILITIES', 'MFA COVERAGE'];
                const config = {
                    type: 'doughnut' as keyof ChartTypeRegistry,
                    data: {
                        datasets: datasets.map((dataset) => ({
                            data: dataset.data.slice(0, 4),
                            backgroundColor: dataset.backgroundColor,
                            borderColor: dataset.borderColor,
                            borderWidth: 1,
                        })),
                        labels: labels,
                    },
                    options: ({
                        layout: {
                            padding: { left: 90, right: 0, top: 10, bottom: 10 },
                        },
                        responsive: true,
                        maintainAspectRatio: false,
                        cutout: '35%',
                        plugins: {
                            legend: {
                                display: true,
                                position: 'right',
                                labels: { fontSize: 2, usePointStyle: true },
                            },
                            customCanvasBackgroundColor: {
                                color: '#FFFFFF !important',
                            },
                            tooltip: {
                                enabled: true,
                                filter: (tooltipItem: any) => {
                                    const dataset = datasets[tooltipItem.datasetIndex];
                                    const value = dataset?.data[tooltipItem.dataIndex];
                                    return value !== 0;
                                },
                                callbacks: {
                                    label: (context: any) => {
                                        const dataset = datasets[context.datasetIndex];
                                        const value = dataset?.data[context.dataIndex];
                                        if (value === 0) {
                                            return null;
                                        }
                                        return ` ${context?.label} : ${value}%`;
                                    },
                                    title: (context: any) => {
                                        const dataset = datasets[context[0]?.datasetIndex];
                                        const value = dataset?.data[context[0]?.dataIndex];
                                        const dataName = dataset?.name;
                                        if (value === 0) {
                                            return null;
                                        }
                                        return dataName;
                                    },
                                },
                                intersect: false,
                                interaction: {
                                    mode: 'nearest',
                                },
                            },
                            datalabels: {
                                formatter: (value: number) => {
                                    if (value <= 0) {
                                        return '';
                                    }
                                    return value + '%';
                                },
                                color: '#000000',
                                font: {
                                    size: 12,
                                },
                                anchor: 'center',
                                align: 'center',
                            },
                        },
                    } as unknown) as ChartOptions,
                };
                chart = new Chart(ctx, config);
                chart.update();
            }
            return () => {
                if (chart) {
                    chart.destroy();
                }
            };
        }
    }, [chartRef, datasets]);
};

export const areaGraphData = (props: AreaGraphProps) => {
    const data = props.series.flatMap((series) => series.data);
    return {
        series: props.series,
        options: {
            chart: {
                height: 350,
                toolbar: {
                    show: false,
                },
            },
            dataLabels: {
                enabled: false,
            },
            xaxis: {
                categories: props.categories,
                labels: {
                    style: {
                        fontFamily: 'Barlow',
                        fontSize: '10px',
                        fontWeight: 700,
                        lineHeight: '14px',
                        colors: '#FFFFFF',
                    },
                },
            },
            yaxis: {
                min: calculateMinYaxisValue(data),
                max: calculateMaxYaxisValue(data),
                tickAmount: 4,
                labels: {
                    style: {
                        fontFamily: 'Barlow',
                        fontSize: '10px',
                        fontWeight: 700,
                        lineHeight: '14px',
                        colors: ['#FFFFFF'],
                    },
                },
            },
            legend: {
                show: false,
            },
            colors: ['#FF2629', '#FF8C22', '#FFFF07', '#65CC01'],
            stroke: {
                width: [2],
                curve: 'straight' as 'straight',
            },
            grid: {
                yaxis: {
                    lines: {
                        show: false,
                    },
                },
            },
            tooltip: {
                enabled: false,
            },
        },
    };
};

export const AMP_CHART_FONT_FAMILY = 'Barlow, sans-serif';
export const AMP_CHART_FONT_COLOR = 'rgba(255, 255, 255, 1.0)';
export const AMP_CHART_FONT_SIZE = 12;
export const AMP_CHART_GRID_HIGH_EMPHASIS = 'rgba(255, 255, 255, 0.5)';
export const AMP_CHART_GRID_LOW_EMPHASIS = 'rgba(255, 255, 255, 0.2)';
export const AMP_CHART_BORDER_COLOR = 'rgba(255, 255, 255, 1.0)';
/** Tick label color is independent of grid color: use this for axis numbers/labels. */
export const AMP_CHART_TICK_LABEL_COLOR = AMP_CHART_FONT_COLOR;
export const AMP_CHART_TICK_CONFIG = {
  color: AMP_CHART_TICK_LABEL_COLOR,
  font: {
    family: AMP_CHART_FONT_FAMILY,
    size: AMP_CHART_FONT_SIZE,
    color: AMP_CHART_TICK_LABEL_COLOR,
  },
};
const AMP_CHART_TITLE_CONFIG = {
  display: false,
  text: '',
  color: AMP_CHART_FONT_COLOR,
  font: {
    family: AMP_CHART_FONT_FAMILY,
    size: 18,
  },
};
const AMP_CHART_TOOLTIP_CONFIG = {
  mode: 'index',
  intersect: false,
  backgroundColor: 'rgba(30, 40, 50, 0.95)',
  titleColor: AMP_CHART_FONT_COLOR,
  titleFont: {
    family: AMP_CHART_FONT_FAMILY,
    size: 12,
  },
  bodyColor: AMP_CHART_FONT_COLOR,
  bodyFont: {
    family: AMP_CHART_FONT_FAMILY,
    size: AMP_CHART_FONT_SIZE,
  },
  borderColor: AMP_CHART_BORDER_COLOR,
  borderWidth: 1,
  padding: 6,
  boxPadding: 4,
  usePointStyle: true,
};
const AMP_CHART_SUBTITLE_CONFIG = {
  display: false,
  text: '',
  color: AMP_CHART_FONT_COLOR,
  padding: 6,
  boxPadding: 4,
  font: {
    family: AMP_CHART_FONT_FAMILY,
    size: 14,
  },
};
const AMP_CHART_LEGEND_CONFIG = {
  display: true,
  position: 'bottom',
  labels: {
    color: AMP_CHART_FONT_COLOR,
  },
  font: {
    family: AMP_CHART_FONT_FAMILY,
    size: 12,
  },
  usePointStyle: true,
  pointStyle: 'circle',
};
export const AMP_CHART_SCALES_X_CONFIG = {
  ticks: AMP_CHART_TICK_CONFIG,
  grid: {
    color: AMP_CHART_GRID_LOW_EMPHASIS,
  },
  border: {
    color: AMP_CHART_BORDER_COLOR,
  },
};
export const AMP_CHART_SCALES_Y_CONFIG = {
  type: 'linear',
  position: 'left',
  ticks: AMP_CHART_TICK_CONFIG,
  grid: {
    color: AMP_CHART_GRID_HIGH_EMPHASIS,
  },
  title: {
    display: true,
    color: AMP_CHART_FONT_COLOR,
    font: {
      family: AMP_CHART_FONT_FAMILY,
      size: AMP_CHART_FONT_SIZE,
    },
  },
};

export const AMP_CHART_SCALES_SECONDARY_Y_CONFIG = {
  ...AMP_CHART_SCALES_Y_CONFIG,
  position: 'right',
  grid: {
    drawOnChartArea: false,
  },
};

export const AMP_CHART_LINE_CONFIG = {
  tension: 0.2,
  borderWidth: 2,
  spanGaps: true,
};

const AmpAnalyticsLineChartTheme = {
  responsive: true,
  maintainAspectRatio: false,
  stacked: false,
  spanGaps: true,
  interaction: {
    mode: 'index',
    intersect: false,
  },
  elements: {
    line: {
      ...AMP_CHART_LINE_CONFIG,
    },
  },
  plugins: {
    tooltip: {
      ...AMP_CHART_TOOLTIP_CONFIG,
    },
    title: {
      ...AMP_CHART_TITLE_CONFIG,
    },
    subtitle: {
      ...AMP_CHART_SUBTITLE_CONFIG,
    },
    legend: {
      ...AMP_CHART_LEGEND_CONFIG,
    },
  },
  scales: {
    x: {
      ...AMP_CHART_SCALES_X_CONFIG,
    },
    y: {
      ...AMP_CHART_SCALES_Y_CONFIG,
    }
  }
};

export function getOptionsForTheme(title?: string, subtitle?: string): Record<string, any> {
  const options = _.cloneDeep(AmpAnalyticsLineChartTheme);
  options.plugins.title.display = title ? true : false;
  options.plugins.title.text = title ?? '';
  options.plugins.subtitle.display = subtitle ? true : false;
  options.plugins.subtitle.text = subtitle ?? '';
  return options;
};